/**
 * Authentication Utilities
 *
 * Read-only helpers around the SSO session. Persistence lives in
 * `src/auth/authSync.ts`; everything here reads the storage keys
 * `syncAuthToLocalStorage()` writes (`token`, `role`, `email`, etc.).
 */

import sso from '@/auth/sso';
import { UserRoleEnum } from '@/types/auth';
import type { CurrentUser } from '@/types';

export interface DecodedJWT {
  exp?: number;
  iat?: number;
  sub?: string;
  email?: string;
  role?: string;
  [key: string]: unknown;
}

export const decodeJWT = (accessToken: string): DecodedJWT | null => {
  try {
    if (!accessToken) return null;

    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const getTokenExpirationFromJWT = (accessToken: string): number | null => {
  const decoded = decodeJWT(accessToken);
  return decoded?.exp ?? null;
};

/**
 * Live token from the SSO provider (always in sync with refreshes),
 * falling back to the persisted copy in localStorage for the brief window
 * between page reload and `sso.init()` completing.
 */
export const getToken = (): string | null =>
  sso.token ?? localStorage.getItem('token');

export const getCurrentRole = (): string => localStorage.getItem('role') ?? '';

export const getCurrentUser = (): CurrentUser | null => {
  if (!sso.authenticated) return null;
  const parsed = sso.tokenParsed as Record<string, string> | undefined;
  return {
    id: parsed?.sub ?? '',
    email: parsed?.email ?? localStorage.getItem('email') ?? '',
    fullName:
      parsed?.name ??
      parsed?.preferred_username ??
      localStorage.getItem('username') ??
      '',
    role: getCurrentRole(),
  };
};

export const hasRole = (requiredRole: string): boolean =>
  getCurrentRole() === requiredRole;

export const hasAnyRole = (requiredRoles: string[]): boolean => {
  const currentRole = getCurrentRole();
  return currentRole ? requiredRoles.includes(currentRole) : false;
};

/**
 * Direct port of `AuthService.checkUserRole()` from `Notify_Angular_Reference`:
 *
 *   checkUserRole(): boolean {
 *     return this.currentUserRole.value == 'SuperUser';
 *   }
 *
 * Used wherever the Notify Angular project gates admin-only UI. Returns true
 * only for SuperUser — Maintenance users get their stricter set via the
 * backend's `[Authorize(Roles = "SuperUser, Maintenance")]` rules.
 */
export const checkUserRole = (): boolean =>
  hasRole(UserRoleEnum[UserRoleEnum.SuperUser]);

/**
 * Less-restrictive companion check: true for any role the Notify backend
 * accepts (SuperUser OR Maintenance). Use this for UI that should be
 * visible to both kinds of authenticated app users.
 */
export const hasAppRole = (): boolean =>
  hasAnyRole([
    UserRoleEnum[UserRoleEnum.SuperUser],
    UserRoleEnum[UserRoleEnum.Maintenance],
  ]);
